import { Room } from '../../../state/rooms.repository';
import { scheduleStatus } from '../../../shared/models/schedule';

/** How many tiles the room login's building-plan preview shows. */
export const PLAN_TILE_COUNT = 3;

export interface PlanPicks {
  /** Up to `limit` rooms to show as tiles, in display order (tile #1 first). */
  rooms: Room[];
  /** Room whose schedule is the globally next current/upcoming occurrence, if any. */
  nextScheduledRoom: Room | null;
  /** How many visible rooms exist in total (browse-list size, before the cap). */
  totalVisible: number;
}

/** Schedules whose occurrence starts within this window count as "imminent". */
const IMMINENT_WINDOW_MS = 60 * 60 * 1000;

/**
 * Picks the rooms for the login's fixed-size building-plan preview. Slots fill
 * from four buckets, deduplicated, in priority order:
 *
 * 1. The imminent room — at most one tile: the scheduled room whose occurrence
 *    is in progress or starts within the next hour, soonest occurrence start
 *    first (ended one-offs don't count).
 * 2. Presence rooms — at least one member online, fullest first.
 * 3. Remaining scheduled rooms, soonest occurrence start first. Imminent rooms
 *    beyond the one-tile cap re-enter here via the dedup.
 * 4. Leftover rooms in store order, regardless of schedule/presence.
 *
 * Capping the imminent bucket at one tile keeps occupied rooms visible even
 * when several meetings are about to start.
 */
export function pickPlanRooms(rooms: Room[], now: number, limit = PLAN_TILE_COUNT): PlanPicks {
  // Hidden rooms (e.g. instant meetings) are never offered in browse lists —
  // they are reachable only by their id (see RoomsStore docs).
  const visible = rooms.filter((room) => !room.isHidden);

  const scheduled = visible
    .filter((room) => room.schedule)
    .map((room) => ({ room, status: scheduleStatus(room.schedule!, now) }))
    .filter(({ status }) => status.state !== 'ended')
    // Stable sort: in-progress occurrences started in the past, so they lead.
    .sort((a, b) => a.status.start - b.status.start);

  // In-progress occurrences have start <= now, so one comparison covers both
  // "already running" and "starts within the window".
  const imminent = scheduled
    .filter(({ status }) => status.start - now <= IMMINENT_WINDOW_MS)
    .slice(0, 1)
    .map(({ room }) => room);

  const presence = visible
    .filter((room) => room.memberCount >= 1)
    .sort((a, b) => b.memberCount - a.memberCount);

  const scheduledRooms = scheduled.map(({ room }) => room);

  const picked: Room[] = [];
  const seen = new Set<string>();
  for (const room of [...imminent, ...presence, ...scheduledRooms, ...visible]) {
    if (picked.length >= limit) break;
    if (seen.has(room.roomId)) continue;
    seen.add(room.roomId);
    picked.push(room);
  }

  return {
    rooms: picked,
    nextScheduledRoom: scheduledRooms[0] ?? null,
    totalVisible: visible.length,
  };
}
